// src/resources/customerRfm/CustomerRfmPage.tsx
//
// Tela "Segmentos de valor" (RFM) — spec
// docs/superpowers/specs/2026-09-29-segmentos-rfm-design.md §6.2, plano
// docs/superpowers/plans/2026-09-29-segmentos-rfm.md Task A3. Chega pelo botão
// "Ver detalhes" do card RFM do Painel 360 (LoyaltySection.tsx), com
// `segment`/`mainPlaceIds` na URL, ou pelo item do submenu "Painel 360", igual
// à tela "Ciclo de vida da base".
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Autocomplete, Box, Button, Chip, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useGetList } from 'react-admin';
import Permission from '../../components/Permission';
import RequiredChain from '../../components/RequiredChain';
import { PERMISSIONS } from '../../constants';
import { getChainId, getPlaceId } from '../../lib';
import ManualLinkButton from '../../manual/components/ManualLinkButton';
import { __t } from '../../i18n/t';
import { COLORS, ErrorCard, SkeletonCard, fmtNum } from '../dashboards/shared';
import { RfmSummary, fetchRfmSummary, refreshRollup } from './api';
import { RFM_MANUAL_SECTION, RfmFlow, RfmGridCell, RfmSegment, SEGMENT_META } from './segments';
import SegmentCards from './SegmentCards';
import RfmGrid from './RfmGrid';
import FlowsPanel from './FlowsPanel';
import SegmentPlaybook from './SegmentPlaybook';
import RfmCustomersTable from './RfmCustomersTable';
import HistoryChart from './HistoryChart';

// Humanizador pt-BR simples ("há X"), mesma receita de Painel360/index.tsx — o plugin
// relativeTime do dayjs não é carregado globalmente.
// i18n: função fora de JSX, o plugin de build não embrulha retorno de template literal —
// por isso os textos vão por __t explícito (mesmo padrão de segments.ts).
const humanizeAgo = (iso: string | null): string | null => {
  if (!iso) return null;
  const then = dayjs(iso);
  if (!then.isValid()) return null;
  const minutes = Math.max(0, dayjs().diff(then, 'minute'));
  if (minutes < 1) return __t('agora');
  if (minutes < 60) return minutes === 1 ? __t('há %{n} minuto', { n: minutes }) : __t('há %{n} minutos', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? __t('há %{n} hora', { n: hours }) : __t('há %{n} horas', { n: hours });
  const days = Math.floor(hours / 24);
  return days === 1 ? __t('há %{n} dia', { n: days }) : __t('há %{n} dias', { n: days });
};

const CustomerRfmPage: React.FC = () => {
  const chainId = getChainId();
  const placeId = getPlaceId();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filtro do card/mapa/fluxo. `segment` e `mainPlaceIds` refletem na URL (pra o link do
  // card e o compartilhamento funcionarem); os demais (r/f/previousSegment/💎) ficam só
  // no estado da tela — sincronizar tudo de ida e volta abriria risco de loop sem ganho
  // real (ninguém compartilha link filtrado por célula do mapa).
  const [segment, setSegment] = useState<RfmSegment | undefined>(() => {
    const v = searchParams.get('segment');
    return (v && v in SEGMENT_META ? (v as RfmSegment) : undefined);
  });
  const [previousSegment, setPreviousSegment] = useState<RfmSegment | 'new' | undefined>();
  const [r, setR] = useState<number | undefined>();
  const [f, setF] = useState<number | undefined>();
  const [highValue, setHighValue] = useState(false);
  // Busca da tabela de clientes (nome/CPF). Precisa entrar no MESMO `filters` que
  // alimenta a tabela, o SegmentPlaybook (criar desconto/pontuar/CSV) e o
  // TagSegmentDialog (etiquetar) — senão essas ações ignoram o termo buscado e
  // agem sobre o segmento inteiro, enquanto a tela mostra a contagem filtrada
  // (bug crítico: ver docs/superpowers/specs/2026-09-29-segmentos-rfm-design.md).
  const [search, setSearch] = useState('');
  const [mainPlaceIds, setMainPlaceIds] = useState<string[]>(() => {
    if (placeId) return [placeId];
    const raw = searchParams.get('mainPlaceIds');
    try { return raw ? JSON.parse(raw) : []; } catch { return []; }
  });

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (segment) next.set('segment', segment); else next.delete('segment');
    if (mainPlaceIds.length) next.set('mainPlaceIds', JSON.stringify(mainPlaceIds)); else next.delete('mainPlaceIds');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment, JSON.stringify(mainPlaceIds)]);

  const { data: stations } = useGetList('stations', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  }, { enabled: !placeId });

  const [summary, setSummary] = useState<RfmSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [summaryError, setSummaryError] = useState(false);
  const [refreshTriggered, setRefreshTriggered] = useState(false);
  // Primeira carga da rede: se o refresh falhar ou passar do tempo normal do rollup (as 5 agregações
  // pesadas somam ~5 min na maior rede), mostra "Tentar de novo" em vez de ficar preso no spinner.
  const [computingSince, setComputingSince] = useState<number | null>(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [filteredTotal, setFilteredTotal] = useState(0);

  const mainPlaceIdsKey = JSON.stringify(mainPlaceIds);

  const loadSummary = useCallback(() => {
    if (!chainId) return;
    setSummaryError(false);
    fetchRfmSummary(chainId, mainPlaceIds.length ? mainPlaceIds : undefined)
      .then(setSummary)
      .catch(() => setSummaryError(true))
      .finally(() => setLoadingSummary(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId, mainPlaceIdsKey]);

  useEffect(() => { setLoadingSummary(true); loadSummary(); }, [loadSummary]);

  useEffect(() => {
    if (!summary?.computing || !chainId) { setComputingSince(null); return; }
    setComputingSince(prev => prev ?? Date.now());
    if (!refreshTriggered) {
      refreshRollup(chainId).catch(() => setRefreshFailed(true));
      setRefreshTriggered(true);
    }
    const timer = setInterval(() => { setNowTick(Date.now()); loadSummary(); }, 20000);
    return () => clearInterval(timer);
  }, [summary?.computing, chainId, refreshTriggered, loadSummary]);

  const computingTooLong = !!computingSince && nowTick - computingSince > 6 * 60 * 1000;
  const retryRefresh = () => {
    if (!chainId) return;
    setRefreshFailed(false);
    setComputingSince(Date.now());
    setNowTick(Date.now());
    refreshRollup(chainId).catch(() => setRefreshFailed(true));
    loadSummary();
  };

  const clearSelection = () => { setSegment(undefined); setPreviousSegment(undefined); setR(undefined); setF(undefined); };

  const handleSelectSegment = (seg: RfmSegment) => {
    if (segment === seg && !previousSegment && !r && !f) { clearSelection(); return; }
    setSegment(seg); setPreviousSegment(undefined); setR(undefined); setF(undefined);
  };

  const handleSelectCell = (cell: RfmGridCell) => {
    if (r === cell.r && f === cell.f) { clearSelection(); return; }
    setR(cell.r); setF(cell.f); setSegment(cell.segment); setPreviousSegment(undefined);
  };

  const handleSelectFlow = (flow: RfmFlow) => {
    const prevKey = flow.from ?? 'new';
    if (segment === flow.to && previousSegment === prevKey && !r && !f) { clearSelection(); return; }
    setSegment(flow.to); setPreviousSegment(prevKey); setR(undefined); setF(undefined);
  };

  const handleSelectNew = () => {
    if (previousSegment === 'new' && !segment && !r && !f) { clearSelection(); return; }
    setSegment(undefined); setPreviousSegment('new'); setR(undefined); setF(undefined);
  };

  const filters = useMemo(() => ({
    chainId: chainId!,
    segment,
    previousSegment,
    r,
    f,
    highValue: highValue || undefined,
    mainPlaceIds: mainPlaceIds.length ? mainPlaceIds : undefined,
    search: search || undefined,
  }), [chainId, segment, previousSegment, r, f, highValue, mainPlaceIdsKey, search]);

  const hasActiveFilter = !!(segment || previousSegment || r || f || highValue);
  const freshnessLabel = humanizeAgo(summary?.computedAt ?? null);
  const baseTotal = summary?.baseTotal ?? 0;
  const rfmTotal = summary?.total ?? 0;

  return (
    <Permission permission={PERMISSIONS.DASHBOARD_V2}>
      <RequiredChain>
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1240, mx: 'auto', width: '100%', boxSizing: 'border-box' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 0.5 }}>
            <Box>
              <Typography sx={{ fontSize: 22, fontWeight: 800 }}>Segmentos de valor</Typography>
              <Typography variant="body2" color="text.secondary">
                {/* i18n: template literal dentro de ternário não é embrulhado pelo plugin de
                    build (só o literal do outro ramo) — __t explícito aqui. */}
                {freshnessLabel ? __t('Atualizado %{freshness} · últimos 12 meses', { freshness: freshnessLabel }) : 'Últimos 12 meses'}
              </Typography>
            </Box>
            <ManualLinkButton slug="dashboard-v2" section={RFM_MANUAL_SECTION} label="Como funciona" />
          </Box>

          {!placeId && (
            <Autocomplete
              multiple size="small" limitTags={2} disableCloseOnSelect
              options={stations ?? []}
              getOptionLabel={(o: any) => o.name ?? o.id}
              value={(stations ?? []).filter((s: any) => mainPlaceIds.includes(s.id))}
              onChange={(_, values: any[]) => setMainPlaceIds(values.map((v) => v.id))}
              sx={{ width: 300, mt: 1.5, mb: 2, '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
              renderInput={(params) => (
                <TextField {...params} label="Posto principal" placeholder={mainPlaceIds.length ? '' : 'Todos os postos'} />
              )}
            />
          )}

          {summaryError && !summary ? (
            <ErrorCard onRetry={loadSummary} height={200} />
          ) : loadingSummary && !summary ? (
            <Grid container spacing={2}>
              {[0, 1, 2].map((i) => <Grid key={i} size={{ xs: 12, sm: 4 }}><SkeletonCard height={140} /></Grid>)}
            </Grid>
          ) : summary?.computing ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6 }}>
              <CircularProgress size={28} sx={{ color: COLORS.brand }} />
              <Typography sx={{ color: COLORS.muted }}>Preparando os dados de fidelidade…</Typography>
              <Typography sx={{ fontSize: 12, color: COLORS.faint }}>A primeira carga da rede leva alguns minutos.</Typography>
              {(refreshFailed || computingTooLong) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Typography sx={{ fontSize: 12.5, color: COLORS.warning, fontWeight: 600 }}>
                    {refreshFailed ? 'Não conseguimos iniciar a preparação dos dados.' : 'A preparação está demorando mais que o normal.'}
                  </Typography>
                  <Button size="small" variant="outlined" onClick={retryRefresh} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
                    Tentar de novo
                  </Button>
                </Box>
              )}
            </Box>
          ) : summary && (
            <>
              <SegmentCards summary={summary} selectedSegment={segment} onSelect={handleSelectSegment} />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.15fr 0.85fr' }, gap: 1.75, mt: 1.75 }}>
                <RfmGrid grid={summary.grid} cutoffs={summary.cutoffs} selectedR={r} selectedF={f} onSelectCell={handleSelectCell} />
                <FlowsPanel
                  flows={summary.flows}
                  left={summary.left}
                  newCustomers={summary.newCustomers}
                  filteredByPlace={summary.filteredByPlace}
                  activeSegment={segment}
                  activePreviousSegment={previousSegment}
                  onSelectFlow={handleSelectFlow}
                  onSelectNew={handleSelectNew}
                />
              </Box>

              <Box sx={{ mt: 1.75 }}>
                <SegmentPlaybook filters={filters} segment={segment} previousSegment={previousSegment} filteredTotal={filteredTotal} />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 2.5, mb: 1 }}>
                <Typography sx={{ fontWeight: 700, mr: 0.5 }}>Clientes</Typography>
                <Chip
                  label={`💎 Somente alto valor`}
                  size="small"
                  onClick={() => setHighValue((v) => !v)}
                  color={highValue ? 'primary' : 'default'}
                  variant={highValue ? 'filled' : 'outlined'}
                />
                {segment && <Chip label={`${SEGMENT_META[segment].emoji} ${__t(SEGMENT_META[segment].label)}`} size="small" onDelete={() => setSegment(undefined)} />}
                {previousSegment && (
                  <Chip
                    label={`Antes: ${previousSegment === 'new' ? __t('novo') : __t(SEGMENT_META[previousSegment].label)}`}
                    size="small"
                    onDelete={() => setPreviousSegment(undefined)}
                  />
                )}
                {(r || f) && <Chip label={`Célula R${r ?? '?'}·F${f ?? '?'}`} size="small" onDelete={() => { setR(undefined); setF(undefined); }} />}
                {hasActiveFilter && <Chip label="Limpar tudo" size="small" variant="outlined" onClick={clearSelection} />}
              </Box>

              <RfmCustomersTable filters={filters} onSearchChange={setSearch} onTotalChange={setFilteredTotal} />

              <Box sx={{ mt: 1.75 }}>
                <HistoryChart history={summary.history} />
              </Box>

              <Typography sx={{ fontSize: 11.5, color: COLORS.faint, mt: 2 }}>
                {fmtNum(rfmTotal)} de {fmtNum(baseTotal)} clientes da base · quem não abasteceu em 12 meses fica de fora
              </Typography>
            </>
          )}
        </Box>
      </RequiredChain>
    </Permission>
  );
};

export default CustomerRfmPage;
